import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";

const toForm = (d) => ({ min_hours: String(d.min_hours), tip_percent: String(d.tip_percent), tiers: d.tiers.map((t) => ({ min_hours: String(t.min_hours), rate: String(t.rate) })) });

export const RentalSettings = () => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/rental-settings").then(({ data }) => setForm((f) => f || toForm(data))); }, []);
  if (!form) return null;
  const setTier = (i, k, v) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { min_hours: Number(form.min_hours), tip_percent: Number(form.tip_percent), tiers: form.tiers.map((t) => ({ min_hours: Number(t.min_hours), rate: Number(t.rate) })) };
      setForm(toForm((await api.put("/admin/rental-settings", body)).data));
      toast.success("Rental settings saved");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="lux-card max-w-2xl space-y-5 p-6" data-testid="rental-settings-form">
      <p className="lux-eyebrow">Hourly / night rentals</p>
      <div className="grid grid-cols-2 gap-4">
        <label className="block"><span className="lux-eyebrow mb-2 block">Minimum hours</span>
          <input type="number" min="1" data-testid="rental-min-hours-input" className="lux-input" value={form.min_hours} onChange={(e) => setForm({ ...form, min_hours: e.target.value })} /></label>
        <label className="block"><span className="lux-eyebrow mb-2 block">Required tip (%)</span>
          <input type="number" min="0" step="0.1" data-testid="rental-tip-input" className="lux-input" value={form.tip_percent} onChange={(e) => setForm({ ...form, tip_percent: e.target.value })} /></label>
      </div>
      {form.tiers.map((t, i) => (
        <div key={i} className="grid grid-cols-2 gap-4">
          <label className="block"><span className="lux-eyebrow mb-2 block">Tier {i + 1} from hours</span>
            <input type="number" min="1" data-testid={`rental-tier-${i}-hours-input`} className="lux-input" value={t.min_hours} onChange={(e) => setTier(i, "min_hours", e.target.value)} /></label>
          <label className="block"><span className="lux-eyebrow mb-2 block">Rate ($/hr)</span>
            <input type="number" min="0" step="0.01" data-testid={`rental-tier-${i}-rate-input`} className="lux-input" value={t.rate} onChange={(e) => setTier(i, "rate", e.target.value)} /></label>
        </div>
      ))}
      <button type="submit" disabled={busy} data-testid="rental-settings-save-button" className="btn-gold">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save rental settings</button>
    </form>
  );
};

const IdRow = ({ row, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [img, setImg] = useState(null);
  const docId = row.id_document_id;
  useEffect(() => {
    if (!docId) return undefined;
    let url;
    api.get(`/id-uploads/${docId}/file`, { responseType: "blob" }).then(({ data }) => { if (data.type.startsWith("image/")) { url = URL.createObjectURL(data); setImg(url); } }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [docId]);
  const view = async () => {
    try {
      const { data } = await api.get(`/id-uploads/${docId}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const mark = async (status) => {
    setBusy(true);
    try { await api.post(`/admin/id-verifications/${docId}`, { status }); toast.success(status === "verified" ? "ID verified" : "ID rejected"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const tid = row.trip_id;
  const x = row.extracted || {};
  return (
    <div data-testid={`id-row-${tid}`} className="flex flex-col gap-3 border-b border-white/5 p-4 last:border-0 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 gap-4 text-sm">
        {img && <img data-testid={`id-image-${tid}`} src={img} alt="Customer ID" className="h-24 w-36 shrink-0 rounded-md border border-white/10 object-cover" />}
        <div className="min-w-0 space-y-0.5">
          <p className="text-amber-100">{row.customer_name} <span className="ml-2 text-xs capitalize text-zinc-500">{row.kind} · {fmtDate(row.created_at)}</span></p>
          <p className="text-zinc-400">Status: <span data-testid={`id-status-${tid}`}>{row.id_status === "pending" ? "Pending" : `ID ${row.id_status}`}</span>
            {row.reviewed_by && <span data-testid={`id-reviewed-${tid}`} className="ml-2 text-xs text-zinc-500">by {row.reviewed_by} · {fmtDate(row.reviewed_at)}</span>}</p>
          <p data-testid={`id-extracted-${tid}`} className="text-xs text-zinc-300">Name on ID: {x.full_name || "—"} · DOB {x.date_of_birth || "—"} · Age {row.age ?? "—"} · Expires {x.expiry_date || "—"}</p>
          <p className="text-xs text-zinc-500">Account name: {row.account_name}</p>
          {row.name_mismatch && <p data-testid={`id-name-mismatch-${tid}`} className="text-xs font-semibold text-amber-400">Name on ID doesn't match the account name</p>}
          {row.check_message && (row.id_status === "pending" || row.check_status !== "ok") && <p data-testid={`id-check-${tid}`} className={`text-xs ${row.check_status === "ok" ? "text-emerald-300" : "text-red-300"}`}>{row.check_message}</p>}
          {row.driver_id_check && <p data-testid={`id-driver-check-${tid}`} className="text-xs text-zinc-400">Driver check: {row.driver_id_check.result === "verified" ? "ID matched" : "ID doesn't match — alcohol not handed over"}</p>}
          {row.food_note && <p className="text-xs text-zinc-500">Request: {row.food_note}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid={`id-view-${tid}`} onClick={view} className="btn-outline-gold !px-4 !py-2 text-xs">View ID</button>
        <button data-testid={`id-verify-${tid}`} disabled={busy || !row.can_approve} title={row.can_approve ? "" : row.check_message} onClick={() => mark("verified")} className="btn-gold !px-4 !py-2 text-xs disabled:cursor-not-allowed disabled:!bg-zinc-700 disabled:!text-zinc-400 disabled:!shadow-none">{row.can_approve ? "ID verified" : "Can't approve"}</button>
        <button data-testid={`id-reject-${tid}`} disabled={busy} onClick={() => mark("rejected")} className="btn-danger !px-4 !py-2 text-xs">ID rejected</button>
      </div>
    </div>
  );
};

export const IdVerificationQueue = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/id-verifications").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="lux-card p-2 sm:p-4" data-testid="id-verification-queue">
      <div className="flex items-center justify-between p-4">
        <p className="lux-eyebrow">ID verification</p>
        <button data-testid="id-queue-refresh" onClick={load} className="text-amber-300 hover:text-amber-200"><RefreshCw className="h-4 w-4" /></button>
      </div>
      {rows?.length === 0 && <p data-testid="id-queue-empty" className="p-4 text-sm text-zinc-500">No ID uploads yet.</p>}
      {rows?.map((r) => <IdRow key={r.trip_id} row={r} onDone={load} />)}
    </div>
  );
};
